import { Fragment, memo } from "react";
import { Table } from "components/layout/tables/Table";
import { GET_SERIES_RANKINGS } from "graphql/series";
import { NewRankingsTableRow } from "./NewRankingsTableRow";
import { useSuspenseQuery } from "@apollo/client";
import { makeStyles } from "components/providers/makeStyles";
import { EmptyState } from "components/layout/EmptyState";
import { Icon } from "styleguide/icons/Icon";
import { T } from "components/util/t";
import { getFilteredList } from "../../../../hooks/useListFilter";
import { CutLine } from "./CutLine";

const useStyles = makeStyles(theme => ({
    table: {
        "& tbody tr": {
            cursor: "pointer"
        },
        "& tbody td": {
            "&:nth-of-type(1)": {
                textAlign: "center",
                minWidth: theme.spacing(3)
            },
            "&:nth-of-type(2)": {
                width: "100%",
                paddingLeft: theme.spacing(1)
            },
            "&:nth-of-type(3)": {
                textAlign: "right",
                whiteSpace: "nowrap"
            },
        }
    }
}));

export const NewRankingsTable = memo(({ selectedSeries, selectedDivision, athleteSearchTerm, filter }) => {
    const classes = useStyles();
    const { data } = useSuspenseQuery(GET_SERIES_RANKINGS, { variables: { id: selectedSeries.id, divisionId: selectedDivision.id, filter }, fetchPolicy: "cache-and-network", nextFetchPolicy: "cache-first" });
    const getAthleteName = item => item.athlete.name;

    const filteredRankings = getFilteredList(data.series.rankings, getAthleteName, athleteSearchTerm);

    const noFilteredRankings = data.series.rankings.length === filteredRankings.length;
    const cutLinesForDivision = [...(selectedSeries.rankingOptions?.cutLines || [])
        .filter(cutLine => cutLine.divisionId.toString() === selectedDivision.id).sort((a, b) => a.position - b.position)];

    if (!data.series.rankings.length) return (
        <EmptyState icon={<Icon icon="Rankings"/>} text="No rankings yet. You'll find the leaderboard here when event results are available."/>
    );

    if (!filteredRankings.length) return (
        <EmptyState text={<T searchTerm={athleteSearchTerm} series={selectedSeries.name} division={selectedDivision.name}>rankings_athelete_search_no_results</T>}/>
    );

    return (
        <Table className={classes.table} height="md" highlightOnHover>
            {filteredRankings.map((ranking, index) => {
                const cutLine = cutLinesForDivision.find(cutLine => cutLine.position < ranking.place && cutLine.position >= filteredRankings[index - 1]?.place);
                return (
                    <Fragment key={index}>
                        {noFilteredRankings && cutLine && <CutLine key={index} index={index} description={cutLine.description} />}
                        <NewRankingsTableRow
                            key={ranking.athlete.id}
                            index={index}
                            ranking={ranking}
                            selectedSeries={selectedSeries}
                            selectedDivision={selectedDivision}
                            filter={filter}
                        />
                    </Fragment>
                );
            })}
            {noFilteredRankings && cutLinesForDivision.filter(cutLine => cutLine.position >= filteredRankings.slice(-1)[0].place).map((cutLine, index) => <CutLine key={cutLine.position} index={index} description={cutLine.description} />)}
        </Table>
    );
});
